import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Popconfirm,
  message,
  Tag,
  Row,
  Col,
  Spin,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API from '../config/api';
import { Package as PackageType, ClinicService } from './type';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import { useLanguage } from './LanguageContext';
import { selectablePackageServices, visibleManualPackages } from '../utils/serviceCatalogSafety';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PackagesManagement: React.FC = () => {
  const [packages, setPackages] = useState<PackageType[]>([]);
  const [services, setServices] = useState<ClinicService[]>([]);
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PackageType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const doctorId = localStorage.getItem('doctorId');

  useEffect(() => {
    fetchPackages();
    fetchServices();
  }, []);

  const fetchPackages = async () => {
    if (!doctorId) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_PACKAGES(doctorId)}`,
        { params: { activeOnly: 'false' } }
      );
      if (response.data.success) {
        setPackages(visibleManualPackages(response.data.data || []));
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      message.error(t('packageLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    if (!doctorId) return;
    try {
      setServicesLoading(true);
      const response = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(doctorId)}`,
        { params: { includeInactive: 'true' } }
      );
      if (response.data.success) {
        setServices(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  const handleAddPackage = () => {
    setEditingPackage(null);
    form.resetFields();
    form.setFieldsValue({
      service_ids: [],
      sessions_count: 1,
      price: 0,
      expiry_days: undefined
    });
    setModalVisible(true);
  };

  const handleEditPackage = (pkg: PackageType) => {
    setEditingPackage(pkg);
    const ids =
      pkg.service_ids && pkg.service_ids.length > 0
        ? pkg.service_ids
        : pkg.service_id
        ? [pkg.service_id]
        : [];
    form.setFieldsValue({
      name: pkg.name,
      service_ids: ids,
      sessions_count: pkg.sessions_count,
      price: pkg.price,
      description: pkg.description || '',
      expiry_days: pkg.expiry_days != null ? pkg.expiry_days : undefined
    });
    setModalVisible(true);
  };

  const handleDeletePackage = async (pkg: PackageType) => {
    if (!doctorId) return;
    try {
      const response = await axios.delete(
        `${API.BASE_URL}${API.ENDPOINTS.PACKAGE(doctorId, pkg.package_id)}`
      );
      if (response.data.success) {
        message.success(t('packageDeleted'));
        fetchPackages();
      }
    } catch (error: any) {
      console.error('Error deleting package:', error);
      message.error(error.response?.data?.message || t('packageDeleteFailed'));
    }
  };

  const handleSubmit = async (values: {
    name: string;
    service_ids: string[];
    sessions_count?: number | null;
    price?: number | null;
    description?: string;
    expiry_days?: number | null;
  }) => {
    if (!doctorId) return;
    const sessionsCount = Number(values.sessions_count);
    const price = Number(values.price);
    if (!Number.isFinite(sessionsCount) || sessionsCount < 1) {
      message.error(t('numberOfSessions') + ' >= 1');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      message.error(t('totalPriceLabel') + ' >= 0');
      return;
    }
    if (!values.service_ids || values.service_ids.length === 0) {
      message.error(t('serviceType') + ' ' + (language === 'ar' ? 'مطلوب' : 'required'));
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        doctor_id: doctorId,
        name: values.name,
        service_ids: values.service_ids,
        sessions_count: sessionsCount,
        price,
        description: values.description || '',
        expiry_days: values.expiry_days != null && Number(values.expiry_days) > 0 ? Number(values.expiry_days) : null
      };

      if (editingPackage) {
        const response = await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.PACKAGE(doctorId, editingPackage.package_id)}`,
          payload
        );
        if (response.data.success) {
          message.success(t('packageUpdated'));
          setModalVisible(false);
          fetchPackages();
        }
      } else {
        const response = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.PACKAGES}`, payload);
        if (response.data.success) {
          message.success(t('packageCreated'));
          setModalVisible(false);
          fetchPackages();
        }
      }
    } catch (error: any) {
      console.error('Error saving package:', error);
      const msg = error.response?.data?.message || error.response?.data?.error;
      message.error(msg || t('packageSaveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const editingServiceIds = editingPackage
    ? (editingPackage.service_ids && editingPackage.service_ids.length > 0
        ? editingPackage.service_ids
        : editingPackage.service_id
          ? [editingPackage.service_id]
          : [])
    : [];
  const servicesForSelection = selectablePackageServices(services, editingServiceIds);

  const columns = [
    {
      title: t('packageName'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: t('service'),
      key: 'service_names',
      render: (_: any, record: PackageType) => {
        const names =
          record.service_names && record.service_names.length > 0
            ? record.service_names
            : record.service_name
            ? [record.service_name]
            : [];
        return (
          <Space size={4} wrap>
            {names.map((n, i) => (
              <Tag key={i} color="blue" style={{ marginBottom: 2 }}>{n}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: t('sessions'),
      dataIndex: 'sessions_count',
      key: 'sessions_count',
      width: 90
    },
    {
      title: t('totalPriceEGP'),
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <Text strong style={{ color: '#52c41a' }}>{price.toLocaleString()} {language === 'ar' ? 'ج' : 'EGP'}</Text>
      )
    },
    {
      title: t('perSessionEGP'),
      dataIndex: 'price_per_session',
      key: 'price_per_session',
      render: (val: number, record: PackageType) => {
        const per = record.price_per_session ?? (record.sessions_count ? record.price / record.sessions_count : 0);
        return <Text>{per.toFixed(2)} {language === 'ar' ? 'ج' : 'EGP'}</Text>;
      }
    },
    {
      title: t('expiry'),
      dataIndex: 'expiry_days',
      key: 'expiry_days',
      width: 90,
      render: (days: number | null) =>
        days != null && days > 0 ? (
          <Tag color="orange">{days} {language === 'ar' ? 'يوم' : 'days'}</Tag>
        ) : (
          <Text type="secondary">{t('noExpiry')}</Text>
        )
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (isActive: boolean) => (
        <Tag color={isActive !== false ? 'green' : 'red'}>
          {isActive !== false ? t('active') : t('inactive')}
        </Tag>
      )
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 140,
      render: (_: any, record: PackageType) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditPackage(record)}>
            {t('edit')}
          </Button>
          <Popconfirm
            title={t('deletePackageConfirm')}
            onConfirm={() => handleDeletePackage(record)}
            okText={language === 'ar' ? 'نعم' : 'Yes'}
            cancelText={t('cancel')}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('delete')}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <Layout>
        <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => {}} isMobile={false} />

        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col>
              <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>
                  {t('backToDashboard')}
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  <GiftOutlined style={{ marginInlineEnd: 8 }} />
                  {t('packageOffers')}
                </Title>
              </Space>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPackage} size="large">
                {t('createPackage')}
              </Button>
            </Col>
          </Row>

          <Card title={t('sessionPackagesTitle')}>
            <Spin spinning={loading}>
              {packages.length === 0 ? (
                <Empty
                  description={t('noPackages')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <Table
                  dataSource={packages}
                  rowKey="package_id"
                  columns={columns}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              )}
            </Spin>
          </Card>
        </Content>
      </Layout>

      <Modal
        title={editingPackage ? t('editPackage') : t('createPackage')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label={t('packageNameLabel')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="service_ids"
            label={t('serviceType')}
            rules={[{ required: true, type: 'array', min: 1, message: language === 'ar' ? 'اختر خدمة واحدة على الأقل' : 'Select at least one service' }]}
          >
            <Select
              mode="multiple"
              loading={servicesLoading}
              showSearch
              optionFilterProp="children"
              placeholder={language === 'ar' ? 'اختر خدمة أو أكثر' : 'Select one or more services'}
            >
              {servicesForSelection.map((s) => (
                <Option key={s.service_id} value={s.service_id} disabled={s.isActive === false}>
                  {s.name} – {s.price.toLocaleString()} {language === 'ar' ? 'ج' : 'EGP'}
                  {s.isActive === false ? (language === 'ar' ? ' — غير نشطة' : ' — Inactive') : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sessions_count"
                label={t('numberOfSessions')}
                initialValue={1}
                rules={[{ required: true }, { type: 'number', min: 1 }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price"
                label={t('totalPriceLabel')}
                initialValue={0}
                rules={[{ required: true }, { type: 'number', min: 0 }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label={t('packageDescription')}>
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="expiry_days" label={t('expiryDaysLabel')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingPackage ? t('update') : t('create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default PackagesManagement;
